import type { ProductSpec, ProductSpecifications } from './types';

// Product specifications from the largest Figma layouts.
const captureModels = {
  id: 'models',
  label: 'Capture model options',
  detail: {
    type: 'models',
    items: [
      {
        name: 'Comic 3',
        description: 'Fast, single-person capture',
      },
      {
        name: 'Comic 4',
        description:
          'Highest fidelity, with multi-character, cameras, and faces',
      },
      {
        name: 'Comic Realtime',
        description:
          'Realtime capture and retargeting for recording or streaming',
      },
    ],
  },
} satisfies ProductSpec;

const generationModels = {
  id: 'models',
  label: 'Generation Models',
  detail: {
    type: 'models',
    items: [
      {
        name: 'Swing Choreo',
        description:
          'Complex direction with faces, hands, and multiple characters',
      },
      {
        name: 'Swing Sketch',
        description:
          'Fast full-body motion for action, blocking, and iteration',
      },
    ],
  },
} satisfies ProductSpec;

const editorModels = {
  id: 'models',
  label: 'Generation Models',
  detail: {
    type: 'models',
    items: [
      {
        name: 'Swing Sketch',
        description:
          'Fast full-body motion for action, blocking, and iteration',
      },
    ],
  },
} satisfies ProductSpec;

const formats = ['.fbx', '.gltf', '.usd', '.bvh', '.mb', '.ma'];

const supportedFrameRates = {
  id: 'frame-rates',
  label: 'Supported frame rates',
  detail: { type: 'text', text: '24, 30, 60, 120' },
} satisfies ProductSpec;

const heading = {
  title: 'Specs and Controls',
  description:
    'Flexible inputs, outputs, and controls allow smooth integration into your pipeline.',
  columns: ['Field', 'Detail'],
} as const;

export const captureSpecifications = {
  ...heading,
  rows: [
    captureModels,
    {
      id: 'capture-data',
      label: 'Capture data',
      detail: {
        type: 'text',
        text: '77 joints/bones for position\nDepth\nCamera position\nScale\nGround contact\nFacial blend shapes',
      },
    },
    {
      id: 'derived-data',
      label: 'Derived Data',
      detail: {
        type: 'text',
        text: 'Velocities\nAccelerations\nJoint angular velocity on request',
      },
    },
    {
      id: 'input-formats',
      label: 'Input formats',
      detail: {
        type: 'models',
        items: [
          { name: 'Video', description: '.mp4, .mov, .zip' },
          { name: 'Camera', description: 'fbx, usd' },
        ],
      },
    },
    {
      id: 'output-formats',
      label: 'Output formats',
      detail: {
        type: 'formats',
        items: ['.fbx', '.gltf', '.usd', '.bvh', '.mb'],
      },
    },
    supportedFrameRates,
    {
      id: 'file-size',
      label: 'File size',
      detail: { type: 'text', text: '250mb or less per clip' },
    },
    {
      id: 'controls',
      label: 'Toggle-able controls',
      detail: {
        type: 'text',
        text: 'Facial capture\nSpline simplification\nStrict foot stabilization\nMesh inclusion',
      },
    },
  ],
} satisfies ProductSpecifications;

export const generationSpecifications = {
  ...heading,
  rows: [
    generationModels,
    {
      id: 'output-data',
      label: 'Output Data',
      detail: {
        type: 'text',
        text: 'Skeleton animation, mesh, translation data',
      },
    },
    {
      id: 'output-formats',
      label: 'Output formats',
      detail: { type: 'formats', items: formats },
    },
    supportedFrameRates,
    {
      id: 'controls',
      label: 'Adjustable controls',
      detail: {
        type: 'text',
        text: 'Duration\nLooping\nCurve smoothing\nFrame rate\nVariation count',
      },
    },
  ],
} satisfies ProductSpecifications;

export const editorSpecifications = {
  ...heading,
  rows: [
    editorModels,
    {
      id: 'edit-inputs',
      label: 'Edit Inputs',
      detail: {
        type: 'text',
        text: 'Poses (IK/FK), paths, prompts, & timeline combining',
      },
    },
    {
      id: 'output-formats',
      label: 'Output formats',
      detail: { type: 'formats', items: [...formats, '.mp4 video'] },
    },
    supportedFrameRates,
    {
      id: 'controls',
      label: 'Adjustable controls',
      detail: {
        type: 'text',
        text: 'Foot locking\nSpline smoothing\nTransition duration\nSoftware packaging (Roblox, Unreal, Blender, Maya, Unity)\nFrame rate',
      },
    },
  ],
} satisfies ProductSpecifications;
